<div class="smart-search">
  <!-- Filters template -->
  <ng-template
    #filtersSection
    let-topic="topic"
    let-keywords="keywords"
    let-perimeter="perimeter"
    let-periodStart="periodStart"
    let-periodEnd="periodEnd"
  >
    <span class="config-title">{{ 'search.smart-search-result.filters-reminder' | translate }}</span>
    <div class="filters">
      @if (topic) {
        <div class="filter-item">
          <span class="me-1">{{ 'search.smart-search-result.topic' | translate }} :</span>
          <span class="filter-value">{{ topic }}</span>
        </div>
      }
      @if (keywords) {
        <div class="filter-item">
          <span class="me-1">{{ 'search.smart-search-result.keywords' | translate }} :</span>
          <span class="filter-value">{{ keywords }}</span>
        </div>
      }

      <div class="filter-item">
        <span class="me-1">{{ 'search.smart-search-result.perimeter' | translate }} :</span>
        <span class="filter-value">{{ perimeter }}</span>
      </div>
      <div class="filter-item">
        <span class="me-1">{{ 'search.smart-search-result.period' | translate }} :</span>
        <span class="filter-value">
          @if (periodStart && periodEnd) {
            {{ 'search.smart-search-result.from' | translate }}
            {{ periodStart | date: 'dd MMM yyyy' : '' : 'fr' }}
            {{ 'search.smart-search-result.to' | translate }}
            {{ periodEnd | date: 'dd MMM yyyy' : '' : 'fr' }}
          } @else {
            {{ 'search.smart-search-result.all-periods' | translate }}
          }
        </span>
      </div>
    </div>
  </ng-template>

  <ng-template
    #promptSection
    let-prompt="prompt"
    let-topic="topic"
    let-keywords="keywords"
    let-perimeter="perimeter"
    let-periodStart="periodStart"
    let-periodEnd="periodEnd"
    let-isCollapsible="isCollapsible"
  >
    @if (isCollapsible) {
      <div class="prompt-wrapper-container">
        <button class="modify-prompt-button" (click)="showPromptContext = !showPromptContext">
          <span class="modify-prompt-text">
            {{
              showPromptContext
                ? ('search.smart-search-result.hide-prompt' | translate)
                : ('search.smart-search-result.modify-prompt' | translate)
            }}
          </span>
          <i class="ph ph-caret-down modify-prompt-chevron" [class.rotated]="!showPromptContext"></i>
        </button>

        <div class="prompt-content-wrapper" [class.visible]="showPromptContext">
          <div class="prompt-inner-frame">
            <app-smart-search-prompt
              [prompt]="prompt"
              [isEditable]="true"
              [allowCopy]="true"
              [showAvailableTags]="true"
              [showRelaunchButton]="true"
              (relaunchAnalysis)="relaunchWithUpdatedPrompt($event)"
              class="prompt-content"
            />
          </div>

          <!-- Filters reminder inside the collapsible section -->
          <div class="filters-section">
            <ng-container
              *ngTemplateOutlet="
                filtersSection;
                context: {
                  topic: topic,
                  keywords: keywords,
                  perimeter: perimeter,
                  periodStart: periodStart,
                  periodEnd: periodEnd,
                }
              "
            ></ng-container>
          </div>
        </div>
      </div>
    } @else {
      <div class="config-container">
        <span class="config-title">{{ 'search.smart-search-result.prompt' | translate }}</span>
        <app-smart-search-prompt
          [prompt]="prompt"
          [isEditable]="false"
          [allowCopy]="false"
          [showAvailableTags]="false"
          class="prompt-content"
        />

        <ng-container
          *ngTemplateOutlet="
            filtersSection;
            context: {
              topic: topic,
              keywords: keywords,
              perimeter: perimeter,
              periodStart: periodStart,
              periodEnd: periodEnd,
            }
          "
        ></ng-container>
      </div>
    }
  </ng-template>

  <!-- Error State -->
  @if (shouldShowErrorState) {
    <div class="error-state-wrapper">
      <ng-container
        *ngTemplateOutlet="
          promptSection;
          context: {
            prompt: promptContent,
            topic: topic,
            keywords: keywords,
            perimeter: perimeter,
            periodStart: periodStart,
            periodEnd: periodEnd,
            isCollapsible: true,
          }
        "
      ></ng-container>
      <div class="error-state-container">
        <ex-empty-state
          [svgIcon]="'close-circle-fill'"
          [title]="errorTitle ? (errorTitle | translate) : ('search.smart-search-result.error-default-title' | translate)"
          [paddingTop]="'unset'"
        >
          <div *exEmptyStateContent class="error-content">
            @if (errorContent) {
              <p class="error-secondary-text">
                {{ errorContent | translate }}
              </p>
            } @else {
              <p class="error-secondary-text">
                {{ 'search.smart-search-result.error-contact-support' | translate }}
              </p>
            }
            @if (!isNoRelevantDocumentsError) {
              <ex-button type="primary" variant="raised" size="medium" icon="arrow-clockwise-fill" (click)="retry()">
                {{ 'common.retry' | translate }}
              </ex-button>
            }
          </div>
        </ex-empty-state>
      </div>
    </div>
  } @else {
    <!-- Main Content -->
    <div class="left-side">
      @if (result?.synthesis) {
        <ng-container
          *ngTemplateOutlet="
            promptSection;
            context: {
              prompt: promptContent,
              topic: topic,
              keywords: keywords,
              perimeter: perimeter,
              periodStart: periodStart,
              periodEnd: periodEnd,
              isCollapsible: true,
            }
          "
        ></ng-container>
      } @else {
        <ng-container
          *ngTemplateOutlet="
            promptSection;
            context: {
              prompt: promptContent,
              perimeter: perimeter,
              periodStart: periodStart,
              periodEnd: periodEnd,
              topic: topic,
              keywords: keywords,
              isCollapsible: false,
            }
          "
        ></ng-container>
      }

      <div class="content">
        <!-- Loading State -->
        @if (step != StepStatusEnum.COMPLETED) {
          <div class="search-loader">
            <mat-icon svgIcon="icon-wave-form"></mat-icon>
            <span>{{ getStepTranslationKey() | translate }}</span>
          </div>
        }

        <!-- Results -->
        @if (result && !isEmptySynthesis) {
          @if (result.title) {
            <span class="result-title">{{ result.title }}</span>
          }

          @if (result.stats) {
            <div class="result-summary">
              <span class="title">{{ 'search.smart-search-result.about-analysis' | translate }}</span>
              <ul>
                <li>
                  <span>
                    {{
                      'search.smart-search-result.documents-analyzed'
                        | translate
                          : {
                              count: totalDocuments,
                              plural: documentsPlural,
                            }
                    }}
                    @if (hasDocumentsBreakdown) {
                      : {{ getDocumentBreakdown() }}
                    }
                  </span>
                </li>
                @if (hasPeriod) {
                  <li>
                    <span>
                      {{
                        'search.smart-search-result.period-covered'
                          | translate
                            : {
                                minDate: formattedMinDate,
                                maxDate: formattedMaxDate,
                              }
                      }}
                    </span>
                  </li>
                }
              </ul>
            </div>
          }

          @if (result.synthesis) {
            <div #synthesisContainer class="result-synthesis">
              <markdown #md class="markdown-content" [data]="result.synthesis" (ready)="onMarkdownRendered(md)"></markdown>
            </div>
          }
        }

        <!-- Synthesis Loading -->
        @if (step != StepStatusEnum.COMPLETED && !result && step !== StepStatusEnum.ERROR) {
          <div class="result-loaders">
            <ngx-skeleton-loader class="loader-1" />
            <ngx-skeleton-loader class="loader-2" />
          </div>

          <!-- Animation -->
          <div class="animation-container"></div>
        }
      </div>
    </div>

    <!-- Sources Sidebar -->
    <div class="right-side">
      <!-- Export Section -->
      @if (result && !isEmptySynthesis) {
        <div class="export-section">
          <h3 class="export-title">{{ 'common.export' | translate }}</h3>
          <div class="export-buttons">
            <ex-button class="export-button" (click)="exportFile('pdf')" iconSrc="assets/images/icon-pdf.png" variant="ghost">
              {{ 'common.export-pdf' | translate }}
            </ex-button>
            <ex-button class="export-button" (click)="exportFile('docx')" iconSrc="assets/images/icon-word.png" variant="ghost">
              {{ 'common.export-word' | translate }}
            </ex-button>
          </div>
        </div>
      }

      <!-- Export Loading -->
      @if (step != StepStatusEnum.COMPLETED && !result && step !== StepStatusEnum.ERROR) {
        <div class="export-section">
          <h3 class="export-title">{{ 'common.export' | translate }}</h3>
          <div class="export-loaders">
            <ngx-skeleton-loader class="export-loader" />
            <ngx-skeleton-loader class="export-loader" />
          </div>
        </div>
      }

      @if (result?.resources?.length) {
        <div class="sources">
          <h2 class="sources-title">
            {{ totalResources }}
            {{ 'search.smart-search-result.sources-used' | translate }}
          </h2>
          @for (source of resourceEntries; track source.type) {
            @if (source.entry.documents.length > 0) {
              <ng-container
                *ngTemplateOutlet="
                  sourceSection;
                  context: {
                    icon: source.entry.icon,
                    title:
                      source.entry.documents.length +
                      ' ' +
                      (getSourceTranslationKey(source.type, source.entry.documents.length) | translate),
                    resources: getVisibleDocs(source.type),
                    hasMore: hasMoreDocs(source.type),
                    showAll: source.entry.showAllDocuments,
                    toggle: source.entry.toggle,
                  }
                "
              >
              </ng-container>
            }
          }
        </div>
      }

      <!-- Sources Loading -->
      @if (step != StepStatusEnum.COMPLETED && !result) {
        <div class="sources">
          <h2 class="sources-title">{{ 'search.smart-search-result.sources' | translate }}</h2>
          <div class="source-loaders">
            <div class="loader-section">
              @for (i of [1, 2, 3, 4]; track i) {
                <ngx-skeleton-loader class="synthesis-loader-line" />
              }
            </div>
            <div class="loader-section">
              @for (i of [1, 2, 3, 4]; track i) {
                <ngx-skeleton-loader class="synthesis-loader-line" />
              }
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>

<!-- Source Section Template -->
<ng-template
  #sourceSection
  let-icon="icon"
  let-title="title"
  let-resources="resources"
  let-hasMore="hasMore"
  let-showAll="showAll"
  let-toggle="toggle"
>
  <div class="source-section">
    <div class="source-section-header">
      <i [class]="icon"></i>
      <span class="source-section-title">{{ title }}</span>
    </div>
    <div class="source-list">
      @for (resource of resources; track resource.doc_id) {
        <ng-container
          *ngTemplateOutlet="
            referenceBadge;
            context: {
              index: getResourceIndex(resource),
              title: truncateTitle(resource.title),
              url: resource.url,
              resource: resource,
              titleAttr: resource.title,
              titleWithoutEm: removeEmTags(resource.title),
              source: resource.source || null,
              date: resource.publication_date ? formatDate(resource.publication_date) : '',
              excerpt: resource.excerpt || '',
            }
          "
        ></ng-container>
      }
    </div>
    @if (hasMore) {
      <ex-button [icon]="showAll ? 'minus-square-fill' : 'plus-square-fill'" variant="ghost" (click)="toggle()" class="see-more-button">
        {{ (showAll ? 'search.smart-search-result.see-less' : 'search.smart-search-result.see-more') | translate }}
      </ex-button>
    }
  </div>
</ng-template>

<!-- Reference Badge Template -->
<ng-template
  #referenceBadge
  let-index="index"
  let-title="title"
  let-url="url"
  let-resource="resource"
  let-titleAttr="titleAttr"
  let-titleWithoutEm="titleWithoutEm"
  let-source="source"
  let-date="date"
  let-excerpt="excerpt"
>
  <span class="source-link-wrapper" (click)="openUrl(url)">
    <span class="source-number reference-badge">{{ index }}</span>
    <span class="source-number reference-badge">-</span>
    @if (titleAttr) {
      <span class="source-title" [innerHTML]="titleWithoutEm"></span>
      <div class="source-reference-tooltip smart-search-tooltip smart-search-tooltip-positioned">
        <div class="tooltip-title" [innerHTML]="titleWithoutEm"></div>
        <div class="tooltip-meta">
          @if (date) {
            <div class="tooltip-date tooltip-date-visible">
              <mat-icon svgIcon="calendar-basic-600"></mat-icon>
              <span>{{ date }}</span>
            </div>
          }
          @if (source && resource.document_type !== 'admin_doc') {
            <div class="tooltip-source tooltip-source-visible">
              <mat-icon svgIcon="docs-basic-600"></mat-icon>
              <span>{{ source }}</span>
            </div>
          }
        </div>
        @if (excerpt) {
          <div class="tooltip-excerpt tooltip-excerpt-visible" [innerHTML]="excerpt"></div>
        }
      </div>
    }
  </span>
</ng-template>

<ng-template #exportInProgressToast let-template="template" let-title="title" let-toast="toast" let-toastService="toastService">
  <div class="awaiting-toast-container no-select">
    <div class="left-part-toast">
      <div class="align-middle justify-content-center">
        <div class="waiting-circle-full"></div>
      </div>
    </div>
    <div class="center-part-toast">
      <div class="title color-primary-500 fw-bold">
        <span class="d-table-cell align-middle">{{ title | translate | uppercase }}</span>
      </div>
      <div class="text fst-italic fw-bold color-basic-800">
        <span class="d-table-cell align-middle">{{ 'search.smart-search-result.awaiting-export-toast-text' | translate }}</span>
      </div>
      <div class="cancel-export color-basic-600 fw-bold d-table">
        <span class="d-table-cell align-middle" (click)="toastService.remove(toast); stopExportAndRemoveExportToast()">{{
          'search.smart-search-result.awaiting-export-toast-cancel' | translate
        }}</span>
      </div>
    </div>
    <div class="right-part-toast">
      <div class="hide-icon" ngbTooltip="{{ 'common.hide' | translate }}" placement="top">
        <mat-icon
          svgIcon="hide-600"
          class="icon-20 strict me-2"
          (click)="
            toastService.hide(
              toast,
              template,
              'toast-export-in-progress toast-shape ' + ('t-e-i-p-' + (result?.id || '')),
              result?.id || '',
              lightToast
            )
          "
        >
        </mat-icon>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #SynthesisExportToast let-toast="toast" let-toastService="toastService">
  <ng-container
    [ngTemplateOutlet]="exportInProgressToast"
    [ngTemplateOutletContext]="{
      toast,
      toastService,
      title: getExportToastTitle(),
      template: SynthesisExportToast,
    }"
  >
  </ng-container>
</ng-template>

<ng-template #lightToast let-toast="toast" let-toastService="toastService">
  <div class="mini-toast-container d-flex">
    <div class="separator"></div>
    <div class="mini-toast" (click)="toastService.unhide(toast, result?.id || '')">
      <div class="mini-waiting-circle d-table-cell align-middle align-content-center">
        <div class="waiting-circle-mini"></div>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #errorInExport>
  <app-toast-error [title]="'search.smart-search-result.export-error-title'" [text]="'search.smart-search-result.export-error-text'">
  </app-toast-error>
</ng-template>
